import {
  BookOpenText,
  ChartPie,
  Handshake,
  MessageCircle,
  NotepadText,
} from "lucide-react";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  EnabledPage,
  FrontendIframePageEnum,
  FrontendPageEnum,
} from "shared/model/pages";

import { Text } from "~/components/text";
import { cn } from "~/util/cn";

type MenuBarPosition = "start" | "end";

export interface ChildNavbarRoute {
  page?: EnabledPage;
  path: string;
  title: string;
  isActive: boolean;
  position: MenuBarPosition;
  isLocked?: boolean;
  onClick?: (path: string) => void;
}

export interface ParentNavbarRoute {
  childRoutes: ChildNavbarRoute[];
  title: string;
  uniqueKey: string;
  position: MenuBarPosition;
  onClick?: (path: string) => void;
}

export type NavbarRoute = ChildNavbarRoute | ParentNavbarRoute;

export const NavLinks: React.FC<{
  routes: NavbarRoute[];
  onClick?: (path: string) => void;
}> = ({ routes, onClick = () => void 0 }) => {
  return (
    <>
      {routes.map((route, index) => (
        <li key={`navbar-routes-${route.title}-${index}`}>
          {isParentRoute(route) ? (
            <ParentNavLink {...route} onClick={onClick} />
          ) : (
            <ChildNavLink {...route} onClick={onClick} />
          )}
        </li>
      ))}
    </>
  );
};

const routesIconsMap = {
  [FrontendPageEnum.CHAT]: <BookOpenText width={24} />,
  [FrontendPageEnum.MAMA_ANSWER]: <MessageCircle width={24} />,
  [FrontendPageEnum.REPORT]: <NotepadText width={24} />,
  [FrontendIframePageEnum.PATIENT_ASSOCIATION]: <Handshake width={24} />,
} as const;

export const getRouteIcon = (route?: string): JSX.Element | null =>
  routesIconsMap[route as keyof typeof routesIconsMap] || null;

const ChildNavLink: React.FC<ChildNavbarRoute> = ({
  page,
  path,
  title,
  isActive,
  onClick = () => void 0,
}) => {
  const Icon =
    getRouteIcon(page?.route) ||
    (page?.route.endsWith("-analysis") && <ChartPie width={16} />) ||
    null;

  return (
    <NavLink
      to={path}
      className={cn(
        "btn-block btn justify-start text-left leading-normal shadow-none",
        "transition-colors duration-500 ease-in-out",
        isActive ? "bg-white hover:bg-base-100" : "bg-transparent",
      )}
      onClick={() => onClick(path)}
    >
      <div
        className={cn(
          isActive ? "opacity-100" : "opacity-80",
          "transition-all",
        )}
      >
        {Icon}
      </div>

      <Text
        text={title}
        className={cn(
          isActive
            ? "font-bold text-neutral-900"
            : "font-medium text-neutral-700",
          "transition-colors duration-500 ease-in-out",
        )}
      />
    </NavLink>
  );
};

const ParentNavLink: React.FC<ParentNavbarRoute> = ({
  title,
  childRoutes,
  onClick,
}) => {
  const [isOpen, setIsOpen] = useState(
    childRoutes.findIndex((route) => route.isActive) > 0,
  );

  return childRoutes.length === 0 ? null : childRoutes.length === 1 ? (
    <ChildNavLink {...childRoutes[0]} title={title} />
  ) : childRoutes.length > 1 ? (
    <div
      className={cn(
        "collapse-arrow collapse cursor-pointer",
        isOpen ? "collapse-open" : "collapse-close",
      )}
    >
      <input
        title={title}
        type="checkbox"
        className="cursor-pointer"
        onClick={() => setIsOpen((prev) => !prev)}
      />
      <div className="btn-ghost collapse-title btn-block btn justify-start">
        <Text as="span" text={title} />
      </div>
      <ul className="collapse-content flex flex-col gap-1.5">
        {childRoutes.map((route) => (
          <li key={route.path}>
            <ChildNavLink key={route.path} {...route} onClick={onClick} />
          </li>
        ))}
      </ul>
    </div>
  ) : childRoutes.length === 1 ? (
    <ChildNavLink {...childRoutes[0]} title={title} onClick={onClick} />
  ) : null;
};

const isParentRoute = (route: NavbarRoute): route is ParentNavbarRoute =>
  !("path" in route);

export const useSplitNavbarRoutes = (
  routes: NavbarRoute[] = [],
): Record<MenuBarPosition, NavbarRoute[]> => {
  return {
    start: routes.filter((route) => route.position === "start"),
    end: routes.filter((route) => route.position === "end"),
  };
};
